<template>
    <div class="bakery-homepage">
        <section class="hero-section position-relative overflow-hidden">
            <img class="hero-corner position-absolute top-0 end-0" src="/img/svg/nav-corner-2.svg" alt="">
            <div class="container position-relative py-5">
                <div class="row align-items-center gy-5 py-lg-5">
                    <div class="col-12 col-lg-6">
                        <p class="section-eyebrow mb-3">{{ $t('marketing.home.eyebrow') }}</p>
                        <h1 class="display-4 fw-bolder text-gray-900 mb-4">{{ $t('marketing.home.title') }}</h1>
                        <p class="hero-copy fs-5 text-gray-600 mb-4">{{ $t('marketing.home.subtitle') }}</p>
                        <div class="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-3">
                            <router-link v-if="is_authorized" to="/console/store" class="btn btn-lg btn-brand px-4 py-3 fw-bold text-white">
                                {{ $t('homepage.open_console') }}
                            </router-link>
                            <router-link v-else to="/login" class="btn btn-lg btn-brand px-4 py-3 fw-bold text-white">
                                {{ $t('marketing.cta.primary') }}
                            </router-link>
                            <a href="#workflow" class="btn btn-lg btn-outline-brand px-4 py-3 fw-bold">
                                {{ $t('marketing.cta.see_how') }}
                            </a>
                        </div>
                        <p v-if="!is_authorized" class="small text-gray-500 mt-3 mb-0">
                            {{ $t('marketing.cta.reassurance') }}
                        </p>
                        <div class="d-flex flex-wrap gap-3 mt-4 small fw-bold text-gray-600">
                            <span v-for="item in heroBenefits" :key="item">
                                <BootstrapIcon class="me-1 align-middle" color="fill-teal-600 text-teal-600" name="check-circle-fill" :size="15" />
                                <span class="align-middle">{{ item }}</span>
                            </span>
                        </div>
                    </div>
                    <div class="col-12 col-lg-6">
                        <div class="product-stage mx-auto" :aria-label="$t('marketing.home.preview.title')">
                            <div class="product-stage__header d-flex align-items-center justify-content-between">
                                <span class="small fw-bold text-gray-700">{{ $t('marketing.home.preview.title') }}</span>
                                <span class="badge rounded-pill bg-teal-100 text-teal-800">{{ $t('marketing.home.preview.today') }}</span>
                            </div>
                            <OrderWidgetSample
                            v-for="order in previewOrders"
                            :key="order.id"
                            :order="order"
                            />
                            <div class="product-stage__footer d-flex justify-content-between align-items-center">
                                <span class="small text-gray-500">{{ $t('marketing.home.preview.next') }}</span>
                                <span class="fw-bolder text-gray-800">{{ $t('marketing.home.preview.order_count') }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="container py-5">
            <div class="row justify-content-center text-center mb-4">
                <div class="col-12 col-lg-8">
                    <p class="section-eyebrow">{{ $t('marketing.home.pain.eyebrow') }}</p>
                    <h2 class="display-6 fw-bolder text-gray-900">{{ $t('marketing.home.pain.title') }}</h2>
                    <p class="fs-5 text-gray-600">{{ $t('marketing.home.pain.subtitle') }}</p>
                </div>
            </div>
            <div class="row g-4">
                <div v-for="pain in pains" :key="pain.title" class="col-12 col-md-6 col-xl-3">
                    <div class="pain-card h-100 p-4">
                        <div class="icon-box icon-box--warm mb-4">
                            <BootstrapIcon :name="pain.icon" :size="25" />
                        </div>
                        <h3 class="h5 fw-bolder text-gray-800">{{ pain.title }}</h3>
                        <p class="text-gray-600 mb-0">{{ pain.description }}</p>
                    </div>
                </div>
            </div>
        </section>

        <section id="workflow" class="workflow-section py-5">
            <div class="container py-lg-5">
                <div class="row align-items-center gy-5">
                    <div class="col-12 col-lg-5">
                        <p class="section-eyebrow">{{ $t('marketing.home.workflow.eyebrow') }}</p>
                        <h2 class="display-6 fw-bolder text-gray-900 mb-3">{{ $t('marketing.home.workflow.title') }}</h2>
                        <p class="fs-5 text-gray-600 mb-4">{{ $t('marketing.home.workflow.subtitle') }}</p>
                        <div v-for="(step, index) in workflow" :key="step.title" class="workflow-step d-flex gap-3 mb-4">
                            <span class="step-number">{{ index + 1 }}</span>
                            <div>
                                <h3 class="h5 fw-bolder mb-1">{{ step.title }}</h3>
                                <p class="text-gray-600 mb-0">{{ step.description }}</p>
                            </div>
                        </div>
                        <router-link :to="{ name: 'how_it_works' }" class="fw-bold text-teal-700">
                            {{ $t('marketing.home.workflow.link') }}
                            <BootstrapIcon class="ms-1 align-middle" name="arrow-right" :size="16" />
                        </router-link>
                    </div>
                    <div class="col-12 col-lg-7">
                        <div class="screenshot-shell">
                            <div class="screenshot-shell__bar"><span></span><span></span><span></span></div>
                            <img class="w-100" :src="'/img/marketing/' + locale + '-bakery-orders.png'" :alt="$t('marketing.home.workflow.image_alt')">
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="container py-5">
            <div class="row justify-content-center text-center mb-5">
                <div class="col-12 col-lg-8">
                    <p class="section-eyebrow">{{ $t('marketing.home.outcomes.eyebrow') }}</p>
                    <h2 class="display-6 fw-bolder text-gray-900">{{ $t('marketing.home.outcomes.title') }}</h2>
                </div>
            </div>
            <div class="row g-4">
                <div v-for="outcome in outcomes" :key="outcome.title" class="col-12 col-md-6 col-lg-4">
                    <div class="outcome-card h-100 p-4">
                        <BootstrapIcon class="mb-3" color="fill-teal-600 text-teal-600" :name="outcome.icon" :size="29" />
                        <h3 class="h5 fw-bolder">{{ outcome.title }}</h3>
                        <p class="text-gray-600 mb-0">{{ outcome.description }}</p>
                    </div>
                </div>
            </div>
        </section>

        <section class="proof-section py-5">
            <div class="container py-lg-4">
                <div class="row justify-content-center text-center mb-5">
                    <div class="col-12 col-lg-8">
                        <p class="section-eyebrow">{{ $t('marketing.home.proof.eyebrow') }}</p>
                        <h2 class="display-6 fw-bolder text-gray-900">{{ $t('marketing.home.proof.title') }}</h2>
                    </div>
                </div>
                <div class="row g-4">
                    <div v-for="testimonial in testimonials" :key="testimonial.company" class="col-12 col-lg-6">
                        <figure class="testimonial-card h-100 mb-0 p-4 p-md-5">
                            <div class="d-flex align-items-center gap-3 mb-4">
                                <div class="testimonial-logo">
                                    <img :src="testimonial.logo" :alt="testimonial.company">
                                </div>
                                <div>
                                    <p class="fw-bolder text-gray-900 mb-0">{{ testimonial.company }}</p>
                                    <p class="small text-gray-500 mb-0">{{ testimonial.location }}</p>
                                </div>
                            </div>
                            <BootstrapIcon class="mb-2" color="fill-teal-300 text-teal-300" name="quote" :size="32" />
                            <blockquote class="fs-5 text-gray-700 mb-4">{{ testimonial.quote }}</blockquote>
                            <figcaption class="small fw-bold text-gray-500">{{ testimonial.author }}</figcaption>
                        </figure>
                    </div>
                </div>
            </div>
        </section>

        <section class="container py-5">
            <div class="comparison-card overflow-hidden">
                <div class="row g-0">
                    <div class="col-12 col-lg-6 p-4 p-md-5 comparison-before">
                        <p class="section-eyebrow text-orange-700">{{ $t('marketing.home.comparison.before_label') }}</p>
                        <h2 class="h3 fw-bolder mb-4">{{ $t('marketing.home.comparison.before_title') }}</h2>
                        <p v-for="item in comparisonBefore" :key="item" class="d-flex gap-3 text-gray-600">
                            <BootstrapIcon class="flex-shrink-0 mt-1" color="fill-orange-500 text-orange-500" name="x-circle" :size="18" />
                            <span>{{ item }}</span>
                        </p>
                    </div>
                    <div class="col-12 col-lg-6 p-4 p-md-5 comparison-after">
                        <p class="section-eyebrow text-teal-800">{{ $t('marketing.home.comparison.after_label') }}</p>
                        <h2 class="h3 fw-bolder mb-4">{{ $t('marketing.home.comparison.after_title') }}</h2>
                        <p v-for="item in comparisonAfter" :key="item" class="d-flex gap-3 text-gray-700">
                            <BootstrapIcon class="flex-shrink-0 mt-1" color="fill-teal-700 text-teal-700" name="check-circle-fill" :size="18" />
                            <span>{{ item }}</span>
                        </p>
                    </div>
                </div>
            </div>
        </section>

        <section class="container py-5">
            <div class="row justify-content-center text-center mb-5">
                <div class="col-12 col-lg-8">
                    <p class="section-eyebrow">{{ $t('marketing.home.faq.eyebrow') }}</p>
                    <h2 class="display-6 fw-bolder text-gray-900">{{ $t('marketing.home.faq.title') }}</h2>
                </div>
            </div>
            <div class="row g-4">
                <div v-for="item in faq" :key="item.question" class="col-12 col-lg-6">
                    <div class="faq-card h-100 p-4">
                        <h3 class="h5 fw-bolder text-gray-900">{{ item.question }}</h3>
                        <p class="text-gray-600 mb-0">{{ item.answer }}</p>
                    </div>
                </div>
            </div>
        </section>

        <section class="container py-5">
            <div class="final-cta text-center p-4 p-md-5">
                <p class="section-eyebrow text-white-50">{{ $t('marketing.home.final.eyebrow') }}</p>
                <h2 class="display-6 fw-bolder text-white mx-auto">{{ $t('marketing.home.final.title') }}</h2>
                <p class="fs-5 text-white-50 mx-auto mb-4">{{ $t('marketing.home.final.subtitle') }}</p>
                <router-link v-if="is_authorized" to="/console/store" class="btn btn-light btn-lg px-5 py-3 fw-bold text-teal-800">
                    {{ $t('homepage.open_console') }}
                </router-link>
                <router-link v-else to="/login" class="btn btn-light btn-lg px-5 py-3 fw-bold text-teal-800">
                    {{ $t('marketing.cta.primary') }}
                </router-link>
                <p v-if="!is_authorized" class="small text-white-50 mt-3 mb-0 d-block w-100">{{ $t('marketing.cta.reassurance') }}</p>
            </div>
        </section>
    </div>
</template>

<script>
import BootstrapIcon from '@/common/directive/BootstrapIcon.vue'
import OrderWidgetSample from '@/components/OrderWidgetSample.vue'

export default {
    components: { BootstrapIcon, OrderWidgetSample },
    computed: {
        locale: function () {
            return this.$i18n.locale
        },
        is_authorized: function () {
            return this.$store.state.is_authorized
        },
        heroBenefits: function () {
            return [1, 2, 3].map(index => this.$t(`marketing.home.hero_benefits.${index}`))
        },
        previewOrders: function () {
            return [
                {
                    id: 'A1042F',
                    productCount: this.$t('marketing.home.preview.order_one'),
                    customer: 'Maria Popescu',
                    pickup: this.$t('marketing.home.preview.pickup_one'),
                    status: 'preparing',
                    statusLabel: this.$t('marketing.home.preview.preparing')
                },
                {
                    id: 'B2087C',
                    productCount: this.$t('marketing.home.preview.order_two'),
                    customer: 'Andrei Ionescu',
                    pickup: this.$t('marketing.home.preview.pickup_two'),
                    status: 'pending',
                    statusLabel: this.$t('marketing.home.preview.pending')
                }
            ]
        },
        pains: function () {
            const icons = ['chat-dots', 'journal-x', 'cash-coin', 'people']
            return icons.map((icon, index) => ({
                icon,
                title: this.$t(`marketing.home.pain.items.${index + 1}.title`),
                description: this.$t(`marketing.home.pain.items.${index + 1}.description`)
            }))
        },
        workflow: function () {
            return [1, 2, 3].map(index => ({
                title: this.$t(`marketing.home.workflow.steps.${index}.title`),
                description: this.$t(`marketing.home.workflow.steps.${index}.description`)
            }))
        },
        outcomes: function () {
            const icons = ['clipboard-check', 'calendar-check', 'people', 'person-lines-fill', 'clock-history', 'phone']
            return icons.map((icon, index) => ({
                icon,
                title: this.$t(`marketing.home.outcomes.items.${index + 1}.title`),
                description: this.$t(`marketing.home.outcomes.items.${index + 1}.description`)
            }))
        },
        testimonials: function () {
            return [
                {
                    company: 'NOIR COFFEE ROASTERS',
                    location: this.$t('marketing.home.proof.noir.location'),
                    quote: this.$t('marketing.home.proof.noir.quote'),
                    author: this.$t('marketing.home.proof.noir.author'),
                    logo: '/img/testimonials/noir-coffee.jpg'
                },
                {
                    company: 'NOTRE PATISSERIE',
                    location: this.$t('marketing.home.proof.notre.location'),
                    quote: this.$t('marketing.home.proof.notre.quote'),
                    author: this.$t('marketing.home.proof.notre.author'),
                    logo: '/img/testimonials/logo-notre-patisserie.svg'
                }
            ]
        },
        comparisonBefore: function () {
            const items = this.$tm('marketing.home.comparison.before')
            return Object.keys(items).map(index => this.$t(`marketing.home.comparison.before.${index}`))
        },
        comparisonAfter: function () {
            const items = this.$tm('marketing.home.comparison.after')
            return Object.keys(items).map(index => this.$t(`marketing.home.comparison.after.${index}`))
        },
        faq: function () {
            return [1, 2, 3, 4, 5, 6].map(index => ({
                question: this.$t(`marketing.home.faq.items.${index}.question`),
                answer: this.$t(`marketing.home.faq.items.${index}.answer`)
            }))
        }
    }
}
</script>

<style scoped lang="scss">
.bakery-homepage {
    --marketing-ink: #17212b;
}

.hero-section {
    background: linear-gradient(145deg, #fff 0%, #f4fbf9 60%, #fff5ef 100%);
    margin-top: -30px;
}

.hero-corner {
    width: min(70vw, 900px);
    opacity: .35;
    pointer-events: none;
}

.section-eyebrow {
    color: #16856a;
    font-size: .78rem;
    font-weight: 800;
    letter-spacing: .12em;
    text-transform: uppercase;
}

.hero-copy {
    line-height: 1.7;
    max-width: 620px;
}

.product-stage {
    background: #fff;
    border: 1px solid rgba(32, 201, 151, .18);
    border-radius: 24px;
    box-shadow: 0 30px 70px rgba(38, 68, 61, .16);
    max-width: 570px;
    padding: 22px;
    transform: rotate(1deg);
}

.product-stage__header,
.product-stage__footer {
    padding: 4px 4px 18px;
}

.product-stage__footer {
    border-top: 1px solid #edf1f0;
    margin-top: 8px;
    padding: 18px 4px 2px;
}

.pain-card,
.outcome-card,
.faq-card,
.testimonial-card {
    background: #fff;
    border: 1px solid #e8eeec;
    border-radius: 18px;
}

.pain-card,
.outcome-card {
    transition: transform .2s ease, box-shadow .2s ease;
}

.pain-card:hover,
.outcome-card:hover {
    box-shadow: 0 16px 35px rgba(39, 61, 56, .09);
    transform: translateY(-3px);
}

.icon-box {
    align-items: center;
    border-radius: 12px;
    display: inline-flex;
    height: 50px;
    justify-content: center;
    width: 50px;
}

.icon-box--warm { background: #fff0e8; color: #d7501d; }

.workflow-section,
.proof-section {
    background: #f7faf9;
}

.workflow-step {
    position: relative;
}

.step-number {
    align-items: center;
    background: #d9f7ee;
    border-radius: 50%;
    color: #126d58;
    display: inline-flex;
    flex: 0 0 36px;
    font-weight: 800;
    height: 36px;
    justify-content: center;
}

.screenshot-shell {
    background: #fff;
    border: 1px solid #dfe7e5;
    border-radius: 18px;
    box-shadow: 0 24px 55px rgba(34, 56, 51, .14);
    overflow: hidden;
}

.screenshot-shell__bar {
    background: #f4f6f5;
    display: flex;
    gap: 6px;
    padding: 12px 15px;
}

.screenshot-shell__bar span {
    background: #cfd7d5;
    border-radius: 50%;
    height: 7px;
    width: 7px;
}

.testimonial-logo {
    align-items: center;
    background: #fff;
    border: 1px solid #e8eeec;
    border-radius: 12px;
    display: flex;
    height: 74px;
    justify-content: center;
    overflow: hidden;
    padding: 8px;
    width: 84px;
}

.testimonial-logo img {
    max-height: 100%;
    max-width: 100%;
}

.testimonial-card blockquote {
    line-height: 1.65;
}

.comparison-card {
    border: 1px solid #e8eeec;
    border-radius: 22px;
    box-shadow: 0 20px 45px rgba(39, 61, 56, .08);
}

.comparison-before { background: #fff9f5; }
.comparison-after { background: #eaf9f4; }

.final-cta {
    background: linear-gradient(135deg, #12755e 0%, #20a982 100%);
    border-radius: 24px;
    box-shadow: 0 20px 45px rgba(18, 117, 94, .22);
}

.final-cta h2 {
    max-width: 780px;
}

@media (max-width: 767.98px) {
    .hero-section .display-4 { font-size: 2.45rem; }
    .product-stage { padding: 14px; transform: none; }
}
</style>
